import { Property } from '../../types';
import api from "../../http";
import { useQuery } from "@tanstack/react-query";
import { useForBindingId } from '../../../forBinding';

interface PropertiesResponse {
    docs: Property[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
    ownerName: string;
}

interface UseGetPropertiesParams {
    page?: number;
    limit?: number;
    filter?: string;
    state?: string | string[];
    sortAsc?: boolean;
    enabled?: boolean;
    fetchAllPages?: boolean;
}

export function useGetProperties({
    page = 1,
    limit = 100,
    filter,
    state,
    sortAsc,
    enabled = true,
    fetchAllPages = false
}: UseGetPropertiesParams = {}) {
    const forBinding = useForBindingId();
    const query = useQuery({
        queryKey: ["properties", forBinding, page, limit, filter, state, sortAsc, fetchAllPages],
        enabled,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey[1] === forBinding ? previousData : undefined,
        queryFn: async () => {
            const fetchPage = async (requestedPage: number) => {
                const params = new URLSearchParams();
                params.set('page', String(requestedPage));
                params.set('limit', String(limit));
                if (forBinding) params.set('forBinding', forBinding);
                if (filter) params.set('filter', filter);
                if (sortAsc !== undefined) params.set('sortAsc', String(sortAsc));
                const states = Array.isArray(state) ? state : state ? [state] : [];
                states.forEach((uf) => params.append('state', uf));
                const { data } = await api.get<PropertiesResponse>('/prop/findAll', {
                    params
                });
                return data;
            };

            const firstPage = await fetchPage(fetchAllPages ? 1 : page);
            if (!fetchAllPages || firstPage.totalPages <= 1) return firstPage;

            const remainingPages = await Promise.all(
                Array.from({ length: firstPage.totalPages - 1 }, (_, index) => fetchPage(index + 2))
            );
            const docs = [firstPage, ...remainingPages].flatMap((result) => result.docs);

            return {
                ...firstPage,
                docs,
                limit: docs.length,
                page: 1,
                totalPages: 1,
                hasPrevPage: false,
                hasNextPage: false,
                prevPage: null,
                nextPage: null,
            };
        }
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
}
